import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import SourceCitationChips from './SourceCitationChips';

// react-markdown/remark-gfmがMarkdown記法から生成するタグ、および
// AI応答内の生HTML(<br>)として明示的に許可するタグ。
// rehype-sanitizeのdefaultSchema.tagNames/attributesには依存せず、
// このコンポーネントのCSS(AiChatPanel.css)が実際にスタイリング対象とする
// タグ・属性のみを明示的に列挙する(パッケージ更新でdefaultSchemaが変わっても
// 表示に影響しないようにするため)。
// AI応答由来のリンク(<a>)はフィッシング等の踏み台になり得るため意図的に許可しない
// (href="javascript:..."はdefaultSchema.protocolsで元々ブロックされるが、
// リンク自体をレンダリングしない方針とする)。<a>はテキストとして表示される。
const ALLOWED_TAG_NAMES = [
  'p', 'br', 'hr',
  'strong', 'em', 'del',
  'h1', 'h2', 'h3', 'h4',
  'ul', 'ol', 'li',
  'code', 'pre', 'blockquote',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
];

const ALLOWED_ATTRIBUTES = {
  th: ['align'],
  td: ['align'],
};

// tagNames/attributes以外(protocols/strip/ancestors等)は意図的に指定しない。
// hast-util-sanitizeの仕様上、schemaに無いトップレベルキーはdefaultSchemaの値が
// 自動的に使われるため(参照: hast-util-sanitizeのREADME
// "If any top-level key is missing in the given schema, the corresponding
// value of the default schema is used.")、defaultSchemaをimportしなくても
// <script>除去やhrefのプロトコル制限等の安全設定は失われない。
const sanitizeSchema = {
  tagNames: ALLOWED_TAG_NAMES,
  attributes: ALLOWED_ATTRIBUTES,
};

/**
 * チャットメッセージ1件分のバブル表示(ユーザー／AI共通)
 *
 * @param {Object} props
 * @param {Object} props.message
 * @param {'user'|'ai'} props.message.role - 発言者。'ai'の場合はMarkdown表示・引用元表示の対象になる
 * @param {string} props.message.content - メッセージ本文（streaming中は逐次追記される途中経過）
 * @param {boolean} [props.message.isClarification] - AIからの聞き返し文かどうか（true時は通常回答と区別して扱う）
 * @param {Array} [props.message.sources] - AI回答の引用元一覧（回答確定時のみセットされる）
 * @param {boolean} [props.message.streaming] - サーバーからの差分ストリーミング受信中かどうか
 * @param {boolean} [props.message.citationsPending] - 本文は確定済みだが、出典（attribute_citations）の
 *   確定を待っている最中かどうか（generate_answer完了〜answer確定イベントの間のみtrue）
 * @param {boolean} [props.message.sourcesUnavailable] - 出典判定のLLM呼び出しに失敗し、
 *   出典情報を確定できなかったかどうか（true時、sourcesは常に空配列）
 * @param {boolean} [props.message.error] - 回答生成中/出典確認中にエラーが発生したことを示す
 *   エラー通知専用メッセージかどうか（true時、contentは常に空文字）
 * @param {number} props.message.timestamp - メッセージ生成時刻（Date.now()のミリ秒値）
 * @param {(pageNumber: number) => void} props.onPageJump - 引用元チップクリック時のPDFページ遷移ハンドラ
 */
const ChatMessage = ({ message, onPageJump }) => {
  const isAi = message.role === 'ai';
  const hasSources = isAi && !message.sourcesUnavailable && message.sources && message.sources.length > 0;
  // 本文の追記（streaming）は既に終わっているが、出典（attribute_citations）はまだ確定していない状態。
  // streamingがfalseでもcitationsPendingがtrueのまま残るのは、出典確認待ち中にエラーが
  // 発生したケース。その場合も表示は維持し、アイコンの回転だけ止める（下記isCitationsIconSpinning）。
  const isCitationsPending = isAi && message.citationsPending;
  const isCitationsIconSpinning = isCitationsPending && message.streaming;
  const isSourcesUnavailable = isAi && message.sourcesUnavailable;
  const isErrored = isAi && message.error;

  return (
    <div className={`message ${isAi ? 'ai' : 'user'}`}>
      <div className="message-sender">
        {isAi ? <span className="ai-badge">AI</span> : 'あなた'}
      </div>
      <div className={`message-bubble${isErrored ? ' message-bubble-error' : ''}`}>
        {message.content && (
          <div
            className={`message-bubble-text${isAi ? ' message-bubble-markdown' : ''}${
              message.streaming && !message.citationsPending ? ' streaming' : ''
            }`}
          >
            {isAi ? (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
              >
                {message.content}
              </ReactMarkdown>
            ) : (
              message.content
            )}
          </div>
        )}
        {isErrored && (
          <div className="message-bubble-text">
            ⚠️ 回答中にエラーが発生しました。「新しい質問を始める」ボタンから再度ご質問ください。
          </div>
        )}
        {isCitationsPending && (
          <div className="ai-citations-pending">
            <span className={`ai-citations-pending-icon${isCitationsIconSpinning ? '' : ' ai-citations-pending-icon-paused'}`}>
              ⏳
            </span>{' '}
            出典を確認しています…
          </div>
        )}
        {isSourcesUnavailable && (
          <div className="ai-source-unavailable-notice">
            ⚠️ 出典情報が確認できませんでした。回答内容は必ず原文をご確認ください。
          </div>
        )}
        {hasSources && (
          <>
            <div className="ai-answer-warning">
              ⚠️ AIの回答は参考情報です。最終的な判断は約款の原文および保険会社への確認をお願いします。
            </div>
            <SourceCitationChips sources={message.sources} onPageJump={onPageJump} />
          </>
        )}
      </div>
      <div className="message-time">{format(message.timestamp, 'HH:mm')}</div>
    </div>
  );
};

export default ChatMessage;
